import React, { useEffect, useState } from 'react';
import { Container, Card, Table, Button, Alert, Badge, Spinner, Form } from 'react-bootstrap';
import {
  useGetOrionSyncQuery,
  useTriggerOrionSyncMutation,
  useSetOrionSyncEnabledMutation,
} from '../app/api';

// Super Admin -> Orion Sync.
//
// Two controls that are deliberately independent:
//
//   * the NIGHTLY SWITCH governs the 07:00 UTC beat schedule and defaults OFF,
//     so the integration only starts running on its own once someone decides it
//     is ready;
//   * RUN NOW works either way, which is what makes an off schedule usable
//     during the build phase rather than a blocker.
//
// Dry run is the default press: it reads Orion and reports what it would write.

const STATUS = {
  queued:  { bg: 'secondary', label: 'Queued' },
  running: { bg: 'info',      label: 'Running' },
  success: { bg: 'success',   label: 'Success' },
  error:   { bg: 'danger',    label: 'Failed' },
};

// The API hands back MySQL DATETIMEs, which are UTC but carry no offset. Parsed
// as-is the browser reads them as LOCAL time and silently shifts every timestamp
// by the viewer's offset — so the Z goes on before parsing, and the result is
// rendered in the viewer's own zone.
function when(ts) {
  if (!ts) return '—';
  const iso = String(ts).replace(' ', 'T');
  const d = new Date(/[Z+]|-\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
}

export default function OrionSync() {
  const { data, isLoading, refetch } = useGetOrionSyncQuery();
  const [trigger, { isLoading: isTriggering }] = useTriggerOrionSyncMutation();
  const [setEnabled, { isLoading: isSaving }] = useSetOrionSyncEnabledMutation();
  const [live, setLive] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const active = data?.active;
  const runs = data?.runs || [];
  const cache = data?.cache || {};
  const lastSuccess = data?.last_success;
  const nightlyEnabled = Boolean(data?.nightly_enabled);

  // Poll only while something is in flight. A full sync pulls a 13-month window
  // across ~20 transaction types and takes minutes; a page left open the rest of
  // the day should not keep asking.
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(refetch, 4000);
    return () => clearInterval(t);
  }, [active, refetch]);

  const go = async () => {
    setError('');
    setNote('');
    try {
      const res = await trigger({ live }).unwrap();
      setNote(res.live
        ? 'Live sync started — the cache will be updated.'
        : 'Dry run started — nothing will be written.');
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not start the sync.');
    }
  };

  const toggleNightly = async (value) => {
    setError('');
    setNote('');
    try {
      await setEnabled({ enabled: value }).unwrap();
      setNote(value
        ? 'Nightly sync enabled — it will next run at 07:00 UTC.'
        : 'Nightly sync disabled. You can still run one by hand below.');
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not change the schedule.');
    }
  };

  if (isLoading) {
    return <Container className="py-4 text-center"><Spinner animation="border" /></Container>;
  }

  return (
    <Container className="py-4">
      <h3 className="mb-1">Orion Sync</h3>
      <p className="text-muted" style={{ maxWidth: '46rem' }}>
        Refreshes the Hub’s copy of Orion asset flows — the cache behind
        Work → Net New AUM. Every run re-pulls a trailing 13-month window rather
        than only the newest month, because Orion restates recent months as late
        custodian data lands. One-way and read-only: nothing here writes to Orion.
      </p>

      {/* The schedule switch, above the manual controls because it is the
          question someone comes to this page to answer. */}
      <Card className="mb-3">
        <Card.Body>
          <Form.Check
            type="switch"
            id="orion-nightly"
            checked={nightlyEnabled}
            disabled={isSaving}
            onChange={e => toggleNightly(e.target.checked)}
            label={
              <>
                <strong>Run automatically every night</strong>
                <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                  {' '}— 07:00 UTC (~2am Central), after Orion’s own overnight
                  custodian reconciliation.
                </span>
              </>
            }
          />
          <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
            {nightlyEnabled
              ? 'The schedule is on. Turning it off stops the nightly run only — Run now below keeps working.'
              : 'The schedule is off, so the data below only changes when someone runs a sync by hand.'}
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex gap-4 flex-wrap mb-3" style={{ fontSize: '0.88rem' }}>
            <div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Last updated</div>
              <strong>{when(lastSuccess?.finished)}</strong>
              <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                {lastSuccess ? 'your local time' : 'never synced'}
              </div>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Household-months cached</div>
              <strong style={{ fontSize: '1.1rem' }}>
                {(cache.rows_cached ?? 0).toLocaleString()}
              </strong>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Months covered</div>
              <strong>
                {cache.first_month
                  ? `${String(cache.first_month).slice(0, 7)} → ${String(cache.last_month).slice(0, 7)}`
                  : '—'}
              </strong>
            </div>
          </div>

          {/* A sync that reports success while the page shows nothing is the
              confusing case, and it has exactly one common cause. */}
          {cache.rows_cached === 0 && (
            <Alert variant="warning" className="py-2" style={{ fontSize: '0.82rem' }}>
              <strong>No flows are cached in this environment.</strong> Net New AUM
              will be empty until a live sync runs. Note that firm and advisor
              attribution needs <code>fsc_households</code> populated first — run
              Super Admin → FSC Sync before this one, or every household lands in
              the unmapped bucket and firm totals come back blank.
            </Alert>
          )}

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {note && <Alert variant="success" dismissible onClose={() => setNote('')}>{note}</Alert>}

          {active ? (
            <Alert variant="info" className="d-flex align-items-center gap-2 mb-0">
              <Spinner animation="border" size="sm" />
              <div>
                <strong>{active.live ? 'Live sync' : 'Dry run'} in progress</strong>
                {' — '}{active.progress || 'starting…'}
                <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                  Started by {active.started_by || 'the nightly schedule'} at {when(active.started)}
                </div>
              </div>
            </Alert>
          ) : (
            <>
              <Form.Check
                type="switch"
                id="orion-live"
                className="mb-2"
                checked={live}
                onChange={e => setLive(e.target.checked)}
                label={
                  <>
                    <strong>Write changes</strong>
                    <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                      {' '}— off means a dry run: reads Orion and reports what it
                      would write, without touching the cache.
                    </span>
                  </>
                }
              />
              <Button variant={live ? 'primary' : 'outline-primary'} onClick={go} disabled={isTriggering}>
                {isTriggering ? <Spinner animation="border" size="sm" /> : <i className="bi bi-arrow-repeat" />}
                {' '}{live ? 'Sync from Orion' : 'Run a dry run'}
              </Button>
              <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
                Pulls 13 months across every configured transaction type; takes a
                few minutes. Safe to repeat — rows are keyed on Orion’s own
                transaction id, so a second run over the same window changes nothing.
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <h5 className="mb-2">Recent runs</h5>
      <Card>
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th style={{ width: '10%' }}>Status</th>
              <th style={{ width: '9%' }}>Mode</th>
              <th style={{ width: '20%' }}>Started</th>
              <th style={{ width: '21%' }}>By</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {runs.map(r => {
              const s = STATUS[r.status] || { bg: 'secondary', label: r.status };
              const d = r.detail;
              return (
                <tr key={r.id}>
                  <td><Badge bg={s.bg}>{s.label}</Badge></td>
                  <td style={{ fontSize: '0.82rem' }}>{r.live ? 'Live' : 'Dry run'}</td>
                  <td style={{ fontSize: '0.82rem' }}>{when(r.started)}</td>
                  <td className="text-muted" style={{ fontSize: '0.78rem' }}>
                    {r.started_by || 'Nightly schedule'}
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {typeof d === 'object' && d !== null ? (
                      <>
                        {(d.rows_fetched ?? 0).toLocaleString()} transaction(s) read
                        {d.households != null && <>, {d.households.toLocaleString()} household(s)</>}
                        {/* `live` arrives as MySQL's 0/1, and `0 && …` renders a
                            literal 0 — hence the Boolean(). */}
                        {Boolean(r.live) && d.rows_written != null && (
                          <> · <strong>{d.rows_written.toLocaleString()} written</strong></>
                        )}
                        {!r.live && <span className="text-muted"> · nothing written</span>}
                        {/* The onboarding-lag flag, in the history as well as on
                            the page — each one is an unfinished onboarding. */}
                        {Boolean(r.live) && d.not_in_fsc?.households > 0 && (
                          <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                            {d.not_in_fsc.households.toLocaleString()} counted household(s) not in Salesforce
                          </div>
                        )}
                      </>
                    ) : (
                      <span className={r.status === 'error' ? 'text-danger' : 'text-muted'}>
                        {d || r.progress || '—'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {runs.length === 0 && (
              <tr><td colSpan={5} className="text-center text-muted py-4">
                No syncs recorded yet.
              </td></tr>
            )}
          </tbody>
        </Table>
      </Card>
    </Container>
  );
}
